import { useMemo, useRef, useState } from 'react';
import * as bd from 'react-basic-design';
import { Tab } from 'react-bootstrap';
import {
    downloadSampleExcelApi,
    importLicensesApi,
    LicenseDTO,
    LicensePreviewDTO,
    makePreviewLicensesFromExcelApi,
} from '../../../api/license-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDownload from '../../../assets/icons/Download';
import SvgUpload from '../../../assets/icons/Upload';
import { BasicModal } from '../../../components/basic/basic-modal';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { LicenseUploadSteps } from './license-upload-steps';
import { LoadingButton } from '../../../components/loading-button';

export const UploadLicenseExcelModal = ({
    show,
    onHide,
    onUploaded,
}: {
    show: boolean;
    onHide: () => void;
    onUploaded: (licenses: LicenseDTO[]) => void;
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [allowedSteps, setAllowedSteps] = useState<number[]>([1]);
    const [previewResult, setPreviewResult] = useState<LicensePreviewDTO[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const columns: IBasicTableColumn<LicensePreviewDTO>[] = useMemo(
        () => [
            {
                Header: <T>license-key</T>,
                name: 'licenseKey',
            },
            {
                Header: <T>product-name</T>,
                name: 'productName',
            },
        ],
        []
    );

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsUploading(true);
        const file = e.target.files?.[0];
        if (!file) return;

        makePreviewLicensesFromExcelApi(file)
            .then((result: { licenses: LicensePreviewDTO[] }) => {
                setPreviewResult(result.licenses);
                setAllowedSteps([1, 2]);
                setCurrentStep(2);
            })
            .finally(() => setIsUploading(false));
    };

    const handleDownloadSample = () => {
        setIsDownloading(true);
        downloadSampleExcelApi()
            .then(({ data, fileName }: any) => {
                const url = window.URL.createObjectURL(data);
                const a = document.createElement('a');
                a.href = url;
                a.target = '_blank';
                a.download = fileName ?? 'license-sample.xlsx';
                document.body.appendChild(a);
                a.click();
                a.remove();
            })
            .finally(() => setIsDownloading(false));
    };

    const handleSubmit = () => {
        importLicensesApi(previewResult).then((result: { licenses: LicenseDTO[] }) => {
            onUploaded(result.licenses);
        });
    };

    return (
        <BasicModal
            show={show}
            onHide={onHide}
            title={
                <bd.Flex align="center" content="between">
                    <T>upload-license-excel-modal</T>
                    <bd.Flex align="center" gap={2}>
                        <LoadingButton
                            variant="outline"
                            size="sm"
                            onClick={handleDownloadSample}
                            icon={<SvgDownload />}
                            label={<T>download-sample-excel</T>}
                            loading={isDownloading}
                        />
                        <bd.Button variant="icon" color="secondary" size="sm" onClick={onHide}>
                            <SvgClose />
                        </bd.Button>
                    </bd.Flex>
                </bd.Flex>
            }
            size="xl"
        >
            <div>
                <Tab.Container defaultActiveKey={currentStep} activeKey={currentStep}>
                    <div className="container-fluid ">
                        <Tab.Content className="my-3">
                            <LicenseUploadSteps
                                allowedSteps={allowedSteps}
                                currentStep={currentStep}
                                onSelectStep={setCurrentStep}
                            />
                        </Tab.Content>
                    </div>
                    {currentStep === 1 && (
                        <Tab.Pane eventKey="1">
                            <input
                                ref={fileInputRef}
                                className="d-none"
                                type="file"
                                onChange={handleFileChange}
                                accept=".xlsx"
                            />
                            <bd.Flex align="center" content="center">
                                <LoadingButton
                                    variant="contained"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    icon={<SvgUpload />}
                                    label={<T>upload-license-excel</T>}
                                    loading={isUploading}
                                />
                            </bd.Flex>
                        </Tab.Pane>
                    )}
                    {currentStep === 2 && (
                        <Tab.Pane eventKey="2">
                            <bd.Flex align="center" content="between">
                                <T>preview-result</T>
                                <bd.Button
                                    disabled={!previewResult.some(x => !x.exists)}
                                    variant="contained"
                                    size="sm"
                                    onClick={handleSubmit}
                                >
                                    <T>submit-license-upload</T>
                                </bd.Button>
                            </bd.Flex>
                            <bd.BasicTable
                                hover
                                compact
                                data={previewResult}
                                columns={columns}
                                className="mx-n3 my-2"
                                cellCSS={({ row }) =>
                                    !row.exists
                                        ? 'bg-success text-success'
                                        : 'bg-danger text-danger'
                                }
                            />
                        </Tab.Pane>
                    )}
                </Tab.Container>
            </div>
        </BasicModal>
    );
};
